import React, { useMemo, useState } from "react";
import {
  Download,
  Calendar,
  ListChecks,
  CalendarCheck,
  CheckCircle2,
  XCircle,
  DollarSign,
  TrendingUp,
  Wallet,
  Users,
  UserPlus,
  RefreshCw,
  Package,
  FileBarChart2,
  FileSpreadsheet,
  Loader2,
  Globe,
  Lock,
} from "lucide-react";
import vendorAnalyticsApi, {
  useVendorReports,
} from "../../services/vendorAnalyticsService";
import CustomSelect from "../../components/shared/CustomSelect";
import ChannelFilterTabs from "../../components/vendor/insights/ChannelFilterTabs";
import ChannelSplitCards from "../../components/vendor/insights/ChannelSplitCards";
import ChannelBadge from "../../components/shared/ChannelBadge";
import {
  VendorPageHeader,
  VendorGuideCallout,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";
import { useToastContext } from "../../contexts/ToastContext";

const REPORT_OPTIONS = [
  { value: "bookings", label: "Bookings" },
  { value: "revenue", label: "Revenue" },
  { value: "clients", label: "Clients" },
  { value: "services", label: "Services" },
];

const PERIOD_OPTIONS = [
  { value: "week", label: "This week" },
  { value: "month", label: "This month" },
  { value: "quarter", label: "This quarter" },
  { value: "year", label: "This year" },
];

const PERIOD_LABELS = {
  week: "Week",
  month: "Month",
  quarter: "Quarter",
  year: "Year",
};

const REPORT_LABELS = {
  bookings: "Bookings",
  revenue: "Revenue",
  clients: "Clients",
  services: "Services",
};

const CHANNEL_LABELS = {
  all: "All channels",
  internal: "Internal",
  external: "External",
};

function MetricCard({ icon: Icon, label, value, tone = "neutral" }) {
  const tones = {
    neutral: {
      wrap: "border-border bg-surface-muted/80",
      icon: "border-border bg-surface text-ink-secondary",
      value: "text-ink",
    },
    success: {
      wrap: "border-emerald-200/80 bg-emerald-50/50",
      icon: "border-emerald-200 bg-surface text-emerald-600",
      value: "text-emerald-700",
    },
    danger: {
      wrap: "border-red-200/80 bg-red-50/50",
      icon: "border-red-200 bg-surface text-red-600",
      value: "text-red-700",
    },
    info: {
      wrap: "border-border/80 bg-surface-muted/50 ",
      icon: "border-border bg-surface text-ink",
      value: "text-ink",
    },
  };
  const t = tones[tone] || tones.neutral;

  return (
    <div className={`rounded-xl border p-4 sm:p-5 ${t.wrap}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
            {label}
          </p>
          <p
            className={`mt-2 text-2xl sm:text-3xl font-bold tracking-tight tabular-nums ${t.value}`}
          >
            {value}
          </p>
        </div>
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${t.icon}`}
        >
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </div>
  );
}

const VendorReports = () => {
  const { success, error: showError } = useToastContext();
  const [reportType, setReportType] = useState("bookings");
  const [period, setPeriod] = useState("month");
  const [channel, setChannel] = useState("all");
  const [exporting, setExporting] = useState(false);

  const { data, isLoading, error } = useVendorReports(
    reportType,
    period,
    channel,
  );
  const reports = data?.data || {};

  const title = useMemo(
    () =>
      `${REPORT_LABELS[reportType] || "Report"} · ${PERIOD_LABELS[period] || period} · ${CHANNEL_LABELS[channel] || "All"}`,
    [reportType, period, channel],
  );

  const handleDownloadExcel = async () => {
    setExporting(true);
    try {
      const res = await vendorAnalyticsApi.exportReportExcel({
        period,
        channel,
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.ms-excel;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vendalist-${channel}-${reportType}-${period}-report.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      success(
        "Excel ready",
        "Multi-sheet workbook downloaded — Summary, By Channel, Services, and Bookings.",
      );
    } catch (err) {
      showError(
        null,
        err?.response?.data?.message || "Could not generate the Excel report.",
      );
    } finally {
      setExporting(false);
    }
  };

  const renderMetrics = () => {
    if (reportType === "bookings") {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <MetricCard
              icon={CalendarCheck}
              label="Total bookings"
              value={reports.total_bookings || 0}
            />
            <MetricCard
              icon={CheckCircle2}
              label="Completed"
              value={reports.completed_bookings || 0}
              tone="success"
            />
            <MetricCard
              icon={XCircle}
              label="Cancelled"
              value={reports.cancelled_bookings || 0}
              tone="danger"
            />
          </div>
          {channel === "all" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MetricCard
                icon={Globe}
                label="Internal bookings"
                value={reports.internal_bookings || 0}
                tone="info"
              />
              <MetricCard
                icon={Lock}
                label="External bookings"
                value={reports.external_bookings || 0}
              />
            </div>
          )}
        </div>
      );
    }

    if (reportType === "revenue") {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <MetricCard
              icon={DollarSign}
              label={
                channel === "external" ? "Collectible revenue" : "Total revenue"
              }
              value={`$${Number(reports.total_revenue || 0).toFixed(2)}`}
            />
            <MetricCard
              icon={TrendingUp}
              label="Average booking"
              value={`$${Number(reports.average_booking || 0).toFixed(2)}`}
              tone="info"
            />
            <MetricCard
              icon={Wallet}
              label="Net earnings"
              value={`$${Number(reports.net_earnings || 0).toFixed(2)}`}
              tone="success"
            />
          </div>
          {channel === "all" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <MetricCard
                icon={Globe}
                label="Internal revenue"
                value={`$${Number(reports.platform_revenue || 0).toFixed(2)}`}
                tone="info"
              />
              <MetricCard
                icon={Lock}
                label="External collectible"
                value={`$${Number(reports.external_revenue || 0).toFixed(2)}`}
              />
              <MetricCard
                icon={Wallet}
                label="External paid offline"
                value={`$${Number(reports.private_offline_earnings || 0).toFixed(2)}`}
                tone="success"
              />
            </div>
          )}
        </div>
      );
    }

    if (reportType === "clients") {
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <MetricCard
              icon={Users}
              label="Total clients"
              value={reports.total_clients || 0}
            />
            <MetricCard
              icon={UserPlus}
              label="New clients"
              value={reports.new_clients || 0}
              tone="info"
            />
            <MetricCard
              icon={RefreshCw}
              label="Repeat clients"
              value={reports.repeat_clients || 0}
              tone="success"
            />
          </div>
          {channel === "all" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MetricCard
                icon={Globe}
                label="Internal (platform)"
                value={reports.internal_clients || 0}
                tone="info"
              />
              <MetricCard
                icon={Lock}
                label="External (guests)"
                value={reports.external_clients || 0}
              />
            </div>
          )}
        </div>
      );
    }

    const topServices = reports.top_services || [];
    if (!topServices.length) {
      return (
        <div className="product-surface-muted border-dashed px-4 py-8 text-center">
          <Package className="mx-auto h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm font-medium text-ink-secondary">
            No service data yet
          </p>
          <p className="mt-1 text-xs text-ink-tertiary">
            Service performance will appear here once you have bookings.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2.5">
        {topServices.map((service, index) => (
          <div
            key={`${service.name}-${index}`}
            className="flex items-center justify-between gap-4 rounded-xl border border-border bg-surface-muted/60 px-4 py-3.5"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-xs font-semibold text-ink-tertiary tabular-nums">
                {index + 1}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-ink truncate">
                    {service.name}
                  </p>
                  {service.channel ? (
                    <ChannelBadge channel={service.channel} />
                  ) : null}
                </div>
                <p className="text-xs text-ink-tertiary">
                  {service.bookings_count || 0} bookings
                </p>
              </div>
            </div>
            <p className="text-sm font-semibold text-ink tabular-nums shrink-0">
              ${Number(service.revenue || 0).toFixed(2)}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <VendorPageHeader
        title="Reports"
        subtitle="Channel-aware business reports — preview here, then download a multi-sheet Excel workbook."
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.reports.title}
        steps={VENDOR_PAGE_GUIDES.reports.steps}
        icon={ListChecks}
      />

      <div className="product-panel overflow-hidden">
        <div className="flex flex-col gap-4 border-b border-border px-4 py-4 sm:px-5 sm:py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3 min-w-0">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border bg-surface-muted text-ink">
                <FileBarChart2 className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-semibold tracking-tight text-ink">
                  {title}
                </h2>
                <p className="mt-0.5 text-xs text-ink-tertiary">
                  Excel includes Summary, By Channel, Services, and
                  booking-level detail.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center shrink-0">
              <div className="w-full sm:w-40">
                <CustomSelect
                  value={reportType}
                  onChange={setReportType}
                  options={REPORT_OPTIONS}
                  size="sm"
                  className="w-full"
                />
              </div>
              <div className="w-full sm:w-40">
                <CustomSelect
                  value={period}
                  onChange={setPeriod}
                  options={PERIOD_OPTIONS}
                  size="sm"
                  className="w-full"
                />
              </div>
              <button
                type="button"
                onClick={handleDownloadExcel}
                disabled={isLoading || Boolean(error) || exporting}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-1.5 text-xs font-medium text-white hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {exporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="h-4 w-4" />
                )}
                {exporting ? "Preparing…" : "Download Excel"}
              </button>
            </div>
          </div>

          <ChannelFilterTabs value={channel} onChange={setChannel} />
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-xl border border-border bg-surface-muted p-5"
                >
                  <div className="h-3 w-24 rounded bg-gray-200 mb-4" />
                  <div className="h-8 w-16 rounded bg-gray-200" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700">
              Failed to load reports. Please try again.
            </div>
          ) : (
            <>
              {channel === "all" && (
                <ChannelSplitCards byChannel={reports.by_channel} />
              )}
              {renderMetrics()}
            </>
          )}
        </div>

        <div className="border-t border-border px-4 py-3 sm:px-5 bg-surface-muted/60">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-ink-tertiary inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              Figures update when you change type, period, or channel.
            </p>
            <p className="text-xs text-ink-tertiary inline-flex items-center gap-1.5">
              <Download className="h-3.5 w-3.5" />
              Opens in Excel / Google Sheets as a formatted workbook.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorReports;
